import { getOpenAIClient, isOpenAIConfigured } from './openaiClient';

/**
 * Generates personalized exercises based on user's assessment data
 * @param {Object} assessmentData - User's assessment data including occupation, seniority, and responses
 * @param {Object} careerPlan - User's career plan data
 * @returns {Promise<Object>} Generated exercises with personalized content
 */
export async function generatePersonalizedExercises(assessmentData, careerPlan = null) {
  try {
    // Check if OpenAI is configured
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured. Please check your VITE_OPENAI_API_KEY environment variable.');
    }

    const openai = getOpenAIClient();
    const { occupation, seniority, industry, location = {}, responses = {}, userId } = assessmentData;
    
    // Create comprehensive prompt based on assessment data
    const systemPrompt = `You are an expert career development coach specializing in creating personalized, actionable exercises for professional growth. Generate specific, measurable exercises based on the user's assessment data.

Focus on creating exercises that are:
- Specific and actionable with clear steps
- Appropriate for the user's current skill level and occupation
- Realistic given their time commitment
- Aligned with their career goals and learning style
- Include evidence requirements and reflection prompts

Return a structured JSON response with the exact format specified.`;

    // Build location context for the prompt
    let locationContext = '';
    if (location?.currentZipCode) {
      locationContext += `- Current Location: ZIP code ${location.currentZipCode}\n`;
    }
    if (location?.locationPreference && location?.locationPreference !== 'current_location') {
      locationContext += `- Location Preference: ${location.locationPreference}\n`;
      if (location?.targetLocation) {
        locationContext += `- Target Location: ${location.targetLocation}\n`;
      }
    } else {
      locationContext += `- Location Preference: Stay in current location\n`;
    }

    // Extract career plan items if available
    let planContext = '';
    if (careerPlan?.sections) {
      planContext = '\nCareer Plan Context:\n';
      careerPlan.sections.forEach(section => {
        if (section.items && section.items.length > 0) {
          planContext += `- ${section.type}: ${section.items.map(item => item.title).join(', ')}\n`;
        }
      });
    }

    const userPrompt = `Create personalized exercises for:
- Occupation: ${occupation}
- Industry: ${industry || 'Technology'}
- Current Level: ${seniority}
${locationContext}- Career Goals: ${responses?.careerGoals || 'Professional advancement and skill development'}
- Current Challenges: ${responses?.challenges || 'Skill gaps and career progression'}
- Preferred Learning Style: ${responses?.learningStyle || 'Mixed approach'}
- Time Commitment: ${responses?.timeCommitment || '3-5 hours per week'}
- Skill Rating: ${responses?.skillRating || 3}/5
- Improvement Areas: ${responses?.improvementAreas?.join(', ') || 'Technical skills, communication'}
${planContext}

Generate 8-12 diverse exercises across these categories:
- Technical Skills (2-3 exercises)
- Leadership & Management (2-3 exercises) 
- Networking & Communication (2-3 exercises)
- Industry Knowledge (1-2 exercises)
- Personal Development (1-2 exercises)

Each exercise should be specific to their role, level, and location.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      max_tokens: 4000,
      temperature: 0.7,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'personalized_exercises',
          schema: {
            type: 'object',
            properties: {
              exercises: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    id: { type: 'string' },
                    title: { type: 'string' },
                    description: { type: 'string' },
                    category: { type: 'string', enum: ['Technical Skills', 'Leadership & Management', 'Networking & Communication', 'Industry Knowledge', 'Personal Development'] },
                    difficulty: { type: 'string', enum: ['Beginner', 'Intermediate', 'Advanced'] },
                    estimatedMinutes: { type: 'number' },
                    steps: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          stepNumber: { type: 'number' },
                          title: { type: 'string' },
                          description: { type: 'string' },
                          estimatedMinutes: { type: 'number' }
                        },
                        required: ['stepNumber', 'title', 'description', 'estimatedMinutes']
                      }
                    },
                    evidence: { type: 'string' },
                    reflection: { type: 'string' },
                    isPremium: { type: 'boolean' },
                    dueDate: { type: 'string' },
                    tags: {
                      type: 'array',
                      items: { type: 'string' }
                    }
                  },
                  required: ['id', 'title', 'description', 'category', 'difficulty', 'estimatedMinutes', 'steps', 'evidence', 'reflection', 'isPremium', 'dueDate', 'tags']
                }
              }
            },
            required: ['exercises'],
            additionalProperties: false,
          },
        },
      },
    });

    let exerciseData = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // Enrich exercises with metadata and due dates
    const enrichedExercises = exerciseData.exercises.map((exercise, index) => ({
      ...exercise,
      userId: userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      completed: false,
      completedAt: null,
      progress: 0,
      dueDate: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toISOString(), // Stagger due dates
      priority: index < 3 ? 'High' : index < 6 ? 'Medium' : 'Low'
    }));

    return {
      success: true,
      data: enrichedExercises,
      metadata: {
        totalExercises: enrichedExercises.length,
        generatedAt: new Date().toISOString(),
        basedOnAssessment: true,
        occupation: occupation,
        seniority: seniority
      }
    };

  } catch (error) {
    console.error('Error generating personalized exercises:', error);
    
    // Provide more specific error messages
    if (error?.message?.includes('not properly configured')) {
      throw error;
    } else if (error?.status === 401) {
      throw new Error('Invalid OpenAI API key. Please check your VITE_OPENAI_API_KEY environment variable.');
    } else if (error?.status === 429) {
      throw new Error('OpenAI API quota exceeded. Please check your usage limits.');
    } else {
      throw new Error('Failed to generate personalized exercises. Please try again later.');
    }
  }
}

/**
 * Generates additional exercises for a specific category
 * @param {string} category - The exercise category
 * @param {Object} assessmentData - User's assessment data
 * @returns {Promise<Object>} Generated exercises for the category
 */
export async function generateCategoryExercises(category, assessmentData) {
  try {
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured.');
    }

    const openai = getOpenAIClient();
    const { occupation, seniority, industry, responses = {} } = assessmentData;

    const systemPrompt = `You are an expert career development coach. Generate 3-5 specific, actionable exercises for the "${category}" category based on the user's profile.

Focus on creating exercises that are:
- Specific to their occupation and seniority level
- Realistic given their time commitment
- Include clear steps and evidence requirements
- Appropriate for their learning style`;

    const userPrompt = `Generate ${category} exercises for:
- Occupation: ${occupation}
- Seniority: ${seniority}
- Industry: ${industry || 'Technology'}
- Time Commitment: ${responses?.timeCommitment || '3-5 hours per week'}
- Learning Style: ${responses?.learningStyle || 'Mixed approach'}`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      max_tokens: 2000,
      temperature: 0.7,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'category_exercises',
          schema: {
            type: 'object',
            properties: {
              exercises: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    id: { type: 'string' },
                    title: { type: 'string' },
                    description: { type: 'string' },
                    category: { type: 'string' },
                    difficulty: { type: 'string', enum: ['Beginner', 'Intermediate', 'Advanced'] },
                    estimatedMinutes: { type: 'number' },
                    steps: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          stepNumber: { type: 'number' },
                          title: { type: 'string' },
                          description: { type: 'string' },
                          estimatedMinutes: { type: 'number' }
                        },
                        required: ['stepNumber', 'title', 'description', 'estimatedMinutes']
                      }
                    },
                    evidence: { type: 'string' },
                    reflection: { type: 'string' },
                    isPremium: { type: 'boolean' },
                    tags: {
                      type: 'array',
                      items: { type: 'string' }
                    }
                  },
                  required: ['id', 'title', 'description', 'category', 'difficulty', 'estimatedMinutes', 'steps', 'evidence', 'reflection', 'isPremium', 'tags']
                }
              }
            },
            required: ['exercises'],
            additionalProperties: false,
          },
        },
      },
    });

    let exerciseData = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // Enrich exercises with metadata
    const enrichedExercises = exerciseData.exercises.map((exercise, index) => ({
      ...exercise,
      userId: assessmentData.userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      completed: false,
      completedAt: null,
      progress: 0,
      dueDate: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toISOString(),
      priority: 'Medium'
    }));

    return {
      success: true,
      data: enrichedExercises
    };

  } catch (error) {
    console.error('Error generating category exercises:', error);
    throw new Error('Failed to generate category exercises. Please try again later.');
  }
}

/**
 * Generates a weekly check-in exercise based on user progress
 * @param {Object} assessmentData - User's assessment data
 * @param {Array} completedExercises - User's completed exercises
 * @returns {Promise<Object>} Generated weekly check-in exercise
 */
export async function generateWeeklyCheckin(assessmentData, completedExercises = []) {
  try {
    if (!isOpenAIConfigured()) {
      throw new Error('OpenAI API is not properly configured.');
    }

    const openai = getOpenAIClient();
    const { occupation, seniority, responses = {} } = assessmentData;

    const systemPrompt = `You are an expert career development coach. Generate a personalized weekly check-in exercise that helps the user reflect on their progress and plan next steps.

The exercise should be:
- Personalized to their occupation and seniority level
- Include reflection questions about their recent progress
- Help them identify wins and areas for improvement
- Guide them in planning their next week's activities`;

    const userPrompt = `Create a weekly check-in exercise for:
- Occupation: ${occupation}
- Seniority: ${seniority}
- Recent Completed Exercises: ${completedExercises.map(ex => ex.title).join(', ') || 'None yet'}
- Career Goals: ${responses?.careerGoals || 'Professional advancement'}

Focus on reflection, planning, and motivation.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      max_tokens: 1500,
      temperature: 0.7,
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'weekly_checkin',
          schema: {
            type: 'object',
            properties: {
              exercise: {
                type: 'object',
                properties: {
                  id: { type: 'string' },
                  title: { type: 'string' },
                  description: { type: 'string' },
                  category: { type: 'string' },
                  difficulty: { type: 'string' },
                  estimatedMinutes: { type: 'number' },
                  steps: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        stepNumber: { type: 'number' },
                        title: { type: 'string' },
                        description: { type: 'string' },
                        estimatedMinutes: { type: 'number' }
                      },
                      required: ['stepNumber', 'title', 'description', 'estimatedMinutes']
                    }
                  },
                  evidence: { type: 'string' },
                  reflection: { type: 'string' },
                  isPremium: { type: 'boolean' },
                  tags: {
                    type: 'array',
                    items: { type: 'string' }
                  }
                },
                required: ['id', 'title', 'description', 'category', 'difficulty', 'estimatedMinutes', 'steps', 'evidence', 'reflection', 'isPremium', 'tags']
              }
            },
            required: ['exercise'],
            additionalProperties: false,
          },
        },
      },
    });

    let checkinData = JSON.parse(response?.choices?.[0]?.message?.content);
    
    // Enrich the exercise with metadata
    const enrichedExercise = {
      ...checkinData.exercise,
      userId: assessmentData.userId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      completed: false,
      completedAt: null,
      progress: 0,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // Due in 1 week
      priority: 'High',
      isWeeklyCheckin: true
    };

    return {
      success: true,
      data: enrichedExercise
    };

  } catch (error) {
    console.error('Error generating weekly check-in:', error);
    throw new Error('Failed to generate weekly check-in. Please try again later.');
  }
}




